import React, { createContext, useContext, useEffect, useState } from "react";
import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { StyleView } from "../../../../core/models/entities/StyleView";
import { Style } from "../../../../core/models/entities/Style";
import { IdLookUps } from "../../../../utils/models/idLookUp";
import { Direction } from "../../design-editor/design-editor-tool/DesignEditorTool.vm";
import { copy } from "../../../../utils/functions/ObjectUtil";
import { ScaledViewInfo } from "../../design-editor/design-view/DesignView.vm";
import {
  LogoPlacement,
  LogoPlacementPosition,
} from "../../design-editor/models/Placement";
import { Marker } from "../../design-editor2/models/marker.model";
import { getOriginalTopLeft } from "../../../../utils/functions/MathUtil";
import {
  getEmptyLogoCoverage,
  getLogoMaskCoverage,
  LogoCoverage,
} from "../../design-editor/design-style-editor/DesignStyleEditor.vm";
import { XnY } from "../../design-editor2/models/x-n-y.model";
import { useStyleViewOperations } from "../../design-editor2/hooks/useStyleViewOperations";
import { getLogoPlacementPosition } from "../../design-editor2/functions/getLogoPlacementPosition";
import { toMarker } from "../../design-editor2/functions/toMaker";
import { capRadianToFullRotation } from "../../design-editor2/functions/capRadianToFullRotation";
import { EditAction } from "../../../../data/models/responses/DesignDto";
import { DesignStatus } from "../../../../core/models/entities/enums/DesignStatus";
import { DebuggablePoint } from "../../design-editor2/contexts/design.context";
import { getCenterOfRotatedLogo } from "../../../../utils/functions/MathUtil";
import { EditorConfig } from "../ExternalDesignEditorScreen";
import { generateUUID } from "../../../../utils/functions/StringUtil";

export interface ExternalDesignProviderContext {
  design: Design;
  addToDesign: (logo: BaseLogo) => void;
  selectedView: StyleView;
  views: StyleView[];
  selectedLogo?: DesignLogo;
  logosOfDesign: DesignLogo[];
  scaledLogosOfDesign: Marker[];
  style: Style;
  lockedLogos: IdLookUps<boolean>;
  lockLogo: (logo: DesignLogo, lock: boolean) => void;
  rotateLogo: (logo: DesignLogo, direction: Direction) => void;
  selectView: (view: StyleView) => void;
  selectLogo: (view: DesignLogo["inDesignId"]) => void;
  imageRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  onImageRefSet: () => void;
  scaledViewInfo?: ScaledViewInfo;
  logoPlacement: IdLookUps<LogoPlacement>;
  parentContainerXnY?: XnY;
  onLogoManipulation: (marker: Marker) => void;
  removeFromDesign: (designLogo: DesignLogo) => void;
  placementErrorMessage: string;
  readonly: boolean;
  debug: boolean;
  lastCoverageChecked?: Marker;
  debugPoints: DebuggablePoint[];
  changeLogoView: (logoId: string, viewId: number) => void;
  config?: EditorConfig;
  apiKey: string;
}

const DEBUG_ENABLED = false;
const DEBUG_LOGO_PLACER =
  process.env.NODE_ENV === "development" && DEBUG_ENABLED;

const ExternalDesignContext = createContext<
  ExternalDesignProviderContext | undefined
>(undefined);

interface ExternalDesignProviderProps {
  children: React.ReactNode;
  design: Design;
  config?: EditorConfig;
  apiKey: string;
}

function ExternalDesignProvider({
  children,
  design,
  config,
  apiKey,
}: ExternalDesignProviderProps): React.ReactElement {
  const [designLogos, setDesignLogos] = useState<DesignLogo[]>(
    design.designLogos,
  );
  const [activeViewMarkers, setActiveViewMarkers] = useState<Marker[]>();
  const [allMarkers, setAllMarkers] = useState<Marker[]>();
  const [selectedLogoId, setSelectedLogoId] =
    useState<DesignLogo["inDesignId"]>();

  const [lastCoverageChecked, setLastCoverageChecked] = useState<Marker>();
  const [debugPoints, setDebugPoints] = useState<DebuggablePoint[]>([]);

  const [lockedLogos, setLockedLogos] = useState<IdLookUps<boolean>>({});
  const [logoPlacement, setLogoPlacement] = useState<IdLookUps<LogoPlacement>>(
    {},
  );

  const {
    fullsizeViewInfoPerView,
    scaledViewInfoPerView,
    parentContainerXnY,
    imageRef,
    selectView,
    selectedView,
    style,
    views,
    containerRef,
    onImageRefSet,
  } = useStyleViewOperations(design.style!);

  async function refreshMarkerPositionStatuses(
    allMarkers: Marker[],
    activeMarkers: Marker[],
  ) {
    if (!selectedView) return;

    for (const marker of allMarkers) {
      const filtered = allMarkers.filter((x) => x.viewId === marker.viewId);
      const { position } = getCoverageAndPositionStatus(
        marker,
        filtered,
        scaledViewInfoPerView[marker.viewId],
      );

      const logoPlacement: LogoPlacement = {
        status: position,
        viewId: marker.viewId,
      };
      setLogoPlacement((x) => ({ ...x, [marker.id]: logoPlacement }));
    }
  }

  useEffect(() => {
    if (
      !selectedView ||
      Object.keys(scaledViewInfoPerView).length != views.length
    )
      return;

    const currentViewMarkers = designLogos
      .filter(
        (x) => x.viewId === selectedView.id && scaledViewInfoPerView[x.viewId],
      )
      .map((dl) => toMarker(dl, scaledViewInfoPerView[dl.viewId]));
    const allMarkers = designLogos
      .filter((dl) => scaledViewInfoPerView[dl.viewId])
      .map((dl) => toMarker(dl, scaledViewInfoPerView[dl.viewId]));
    setActiveViewMarkers(currentViewMarkers);
    setAllMarkers(allMarkers);
    refreshMarkerPositionStatuses(allMarkers, currentViewMarkers).then();
  }, [scaledViewInfoPerView, selectedView, designLogos]);

  function addToDesign(logo: BaseLogo) {
    if (!selectedView) return;

    const viewId = selectedView.id;
    const viewInfo = fullsizeViewInfoPerView[selectedView.id];
    const ppcm = viewInfo.ppcm;
    const logoHeightPx = logo.heightMm * (ppcm / 10);
    const logoWidthPx = (logo.designerWidthMm ?? logo.widthMm) * (ppcm / 10);
    const initialX = Math.max(0, viewInfo.width / 2 - logoWidthPx / 2);
    const initialY = Math.max(0, viewInfo.height / 2 - logoHeightPx / 2);
    const designLogo: DesignLogo = {
      id: 0,
      inDesignId: generateUUID(),
      logoId: logo.id,
      logo: logo,
      viewId: viewId,
      logoX: initialX,
      logoY: initialY,
      angleRads: 0,
      widthMm: logo.widthMm,
      designerWidthMm: logo.designerWidthMm,
      heightMm: logo.heightMm,
      action: EditAction.Move,
      viewName: selectedView.view,
    };
    const marker = toMarker(designLogo, scaledViewInfoPerView[selectedView.id]);

    const { position } = getCoverageAndPositionStatus(
      marker,
      activeViewMarkers!,
      scaledViewInfoPerView[viewId],
    );

    setDesignLogos((ls) => [...updateLogosInDesign(logo, ls), designLogo]);
    setActiveViewMarkers((ls) => (ls ? [...ls, marker] : [marker]));

    const logoPlacement: LogoPlacement = {
      status: position,
      viewId: marker.viewId,
    };
    setLogoPlacement((x) => ({ ...x, [marker.id]: logoPlacement }));
  }

  // Effect to handle logos that need centering (sentinel value -99999)
  useEffect(() => {
    // Check if we have logos with sentinel values
    const pendingLogos = designLogos.filter(
      (l) => l.logoX === -99999 && l.logoY === -99999,
    );
    if (pendingLogos.length === 0) return;

    // Check if we have view info for these logos
    const readyLogos = pendingLogos.filter(
      (l) => fullsizeViewInfoPerView[l.viewId],
    );
    if (readyLogos.length === 0) return;

    let updatedDesignLogos = [...designLogos];
    let hasChanges = false;

    readyLogos.forEach((logo) => {
      const viewInfo = fullsizeViewInfoPerView[logo.viewId];
      const ppcm = viewInfo.ppcm;
      const logoHeightPx = logo.heightMm * (ppcm / 10);
      const logoWidthPx = (logo.designerWidthMm ?? logo.widthMm) * (ppcm / 10);

      const centeredX = Math.max(0, viewInfo.width / 2 - logoWidthPx / 2);
      const centeredY = Math.max(0, viewInfo.height / 2 - logoHeightPx / 2);

      updatedDesignLogos = updatedDesignLogos.map((dl) => {
        if (dl.inDesignId === logo.inDesignId) {
          return {
            ...dl,
            logoX: centeredX,
            logoY: centeredY,
          };
        }
        return dl;
      });
      hasChanges = true;
    });

    if (hasChanges) {
      setDesignLogos(updatedDesignLogos);
    }
  }, [designLogos, fullsizeViewInfoPerView]);

  function removeFromDesign(designLogo: DesignLogo) {
    const removeId = designLogo.inDesignId;

    setDesignLogos((ls) => ls.filter((l) => l.inDesignId !== removeId));
    setActiveViewMarkers((ms) => {
      const result = ms ? ms.filter((m) => m.id !== removeId) : [];
      refreshMarkerPositionStatuses(result!, result!).then();
      return result;
    });
    setLogoPlacement((x) => {
      delete x[removeId];
      return x;
    });
  }

  function updateLogosInDesign(logo: BaseLogo, designLogos: DesignLogo[]) {
    const filteredDesignLogos = designLogos.map((x) => {
      if (x.logo.id === logo.id) {
        return { ...x, logo: logo };
      }
      return x;
    });
    return filteredDesignLogos;
  }

  function handleLockLogo(logo: DesignLogo, lock: boolean) {
    setLockedLogos((x) => ({ ...x, [logo.inDesignId]: lock }));
  }

  function rotateLogo(logo: DesignLogo, direction: Direction) {
    const rotateId = logo.inDesignId;
    const markerToRotate = activeViewMarkers?.find((m) => m.id === rotateId);
    if (!markerToRotate) return;

    const currentDeg = markerToRotate.rotationRad * (180 / Math.PI);
    const step = 15;
    const epsilon = 0.05; // Tolerance for floating point/minor inaccuracies

    let targetDeg = 0;

    if (direction === Direction.Right) {
      // Snap to next 15 degrees
      targetDeg = (Math.floor(currentDeg / step) + 1) * step;
      // If we are practically already at the target (e.g. 14.99 -> 15), skip to next
      if (targetDeg - currentDeg < epsilon) {
        targetDeg += step;
      }
    } else {
      // Snap to prev 15 degrees
      targetDeg = (Math.ceil(currentDeg / step) - 1) * step;
      // If we are practically already at the target (e.g. 15.01 -> 15), skip to prev
      if (currentDeg - targetDeg < epsilon) {
        targetDeg -= step;
      }
    }

    markerToRotate.rotationRad = capRadianToFullRotation(
      targetDeg * (Math.PI / 180),
    );
    onLogoManipulation(markerToRotate).then();
  }

  async function onLogoManipulation(marker: Marker) {
    const logo = designLogos.find((dl) => marker.id === dl.inDesignId);
    if (!logo) return;

    const scaledInfo = scaledViewInfoPerView[logo.viewId];
    if (!scaledInfo) return;

    const { styleToViewSizeDelta: sizeDiff, ppcm } = scaledInfo;
    if (!sizeDiff || !ppcm) return;

    const { x: ox, y: oy } = getOriginalTopLeft(
      marker.x,
      marker.y,
      marker.widthPx,
      marker.heightPx,
      0,
    );
    const updatedLogo = copy(logo!);
    updatedLogo.angleRads = marker.rotationRad;
    updatedLogo.logoX = ox / sizeDiff;
    updatedLogo.logoY = oy / sizeDiff;
    updatedLogo.heightMm = marker.heightPx / (ppcm / 10);
    updatedLogo.widthMm = marker.widthPx / (ppcm / 10);

    const { position, coverage } = getCoverageAndPositionStatus(
      marker,
      activeViewMarkers!,
      scaledInfo,
    );

    setDesignLogos((dls) =>
      dls?.map((dl) => {
        if (dl.inDesignId === updatedLogo.inDesignId) return updatedLogo;
        return dl;
      }),
    );

    setActiveViewMarkers((x) =>
      x?.map((y) => {
        if (y.id === marker.id) return marker;
        return y;
      }),
    );

    const logoPlacement: LogoPlacement = {
      status: position,
      viewId: marker.viewId,
    };
    setLogoPlacement((x) => ({ ...x, [marker.id]: logoPlacement }));
  }

  function getCoverageAndPositionStatus(
    marker: Marker,
    overlappingMarkers: Marker[],
    scaledViewInfo: ScaledViewInfo,
  ): { position: LogoPlacementPosition; coverage: LogoCoverage } {
    const {
      height: viewHeight,
      width: viewWidth,
      maskImage,
      maskCanvas,
      maskAvailable,
    } = scaledViewInfo;
    const { x, y, heightPx, widthPx, rotationRad } = marker;

    if (maskAvailable === false) {
      const coverage: LogoCoverage = {
        base: 1,
        pocket: 0,
        collar: 0,
        sewing: 0,
        button: 0,
        zipper: 0,
        background: 0,
        crease: 0,
        reflection: 0,
      };
      const position = getLogoPlacementPosition(
        marker,
        overlappingMarkers,
        coverage,
      );
      return { coverage, position };
    }

    const ctx = maskCanvas.getContext("2d", { willReadFrequently: true });
    if (!ctx)
      return {
        position: LogoPlacementPosition.UNKNOWN,
        coverage: getEmptyLogoCoverage(),
      };

    const coverage = getLogoMaskCoverage(x, y, heightPx, widthPx, rotationRad, {
      maskCanvasCtx: ctx,
      maskImage: maskImage,
      width: viewWidth,
      height: viewHeight,
    });
    const position = getLogoPlacementPosition(
      marker,
      overlappingMarkers,
      coverage,
    );

    if (DEBUG_LOGO_PLACER) {
      const m = { x, y } as DebuggablePoint;
      const offsetRadians = 0;
      const c = getCenterOfRotatedLogo(
        x,
        y,
        widthPx,
        heightPx,
        offsetRadians,
      ) as DebuggablePoint;
      c.color = "gold";
      const o = getOriginalTopLeft(
        x,
        y,
        widthPx,
        heightPx,
        offsetRadians,
      ) as DebuggablePoint;
      o.color = "black";
      const points: DebuggablePoint[] = [m, c, o];
      const getDebuggableMarkerFromXY = (x: number, y: number): Marker => ({
        ...marker,
        x,
        y,
      });
      const debuggableMarker = getDebuggableMarkerFromXY(o.x, o.y);
      setLastCoverageChecked(debuggableMarker);
      setDebugPoints(points);
    }

    return { coverage, position };
  }

  function buildPlacementErrorMessage(): string {
    let errorMsg = "";
    let hasInvalid = false;
    const maskUnavailable =
      !!selectedView &&
      scaledViewInfoPerView[selectedView.id]?.maskAvailable === false;
    if (maskUnavailable)
      return "This style hasn’t been annotated yet. This means that the scale of a logo might not be correct.";

    for (const key in logoPlacement) {
      if (
        logoPlacement[key].status === LogoPlacementPosition.INVALID ||
        logoPlacement[key].status === LogoPlacementPosition.OVERLAPPING
      ) {
        hasInvalid = true;
        break;
      }
    }

    if (hasInvalid)
      errorMsg =
        "A logo in your design seems to have an invalid placement. You can still create the design.";
    return errorMsg;
  }

  function changeLogoView(logoId: string, viewId: number) {
    const logoToMove = designLogos.find((l) => l.inDesignId === logoId);
    if (!logoToMove) return;

    const targetView = views.find((v) => v.id === viewId);
    if (!targetView) return;

    // Remove from current view placement logic
    const oldViewId = logoToMove.viewId;

    // Update logo
    const updatedLogo = copy(logoToMove);
    updatedLogo.viewId = viewId;
    updatedLogo.viewName = targetView.view;

    // Center in new view
    const viewInfo = fullsizeViewInfoPerView[viewId];
    if (viewInfo) {
      const ppcm = viewInfo.ppcm;
      const logoHeightPx = updatedLogo.heightMm * (ppcm / 10);
      const logoWidthPx =
        (updatedLogo.designerWidthMm ?? updatedLogo.widthMm) * (ppcm / 10);
      updatedLogo.logoX = Math.max(0, viewInfo.width / 2 - logoWidthPx / 2);
      updatedLogo.logoY = Math.max(0, viewInfo.height / 2 - logoHeightPx / 2);
    }

    setDesignLogos((ls) =>
      ls.map((l) => (l.inDesignId === logoId ? updatedLogo : l)),
    );

    // If we moved it out of the current view, we need to update active markers
    if (selectedView && selectedView.id === oldViewId) {
      setActiveViewMarkers((ms) => ms?.filter((m) => m.id !== logoId));
      setLogoPlacement((x) => {
        delete x[logoId];
        return { ...x };
      });
    }

    // If we moved it INTO the current view (unlikely if triggered from current view, but possible)
    if (selectedView && selectedView.id === viewId) {
      const marker = toMarker(updatedLogo, scaledViewInfoPerView[viewId]);
      setActiveViewMarkers((ms) => (ms ? [...ms, marker] : [marker]));
      // Recalculate position for new marker
      const { position } = getCoverageAndPositionStatus(
        marker,
        activeViewMarkers || [], // Use current active markers
        scaledViewInfoPerView[viewId],
      );
      setLogoPlacement((x) => ({
        ...x,
        [marker.id]: { status: position, viewId },
      }));
    }

    // Select the target view to follow the logo
    selectView(targetView);
  }

  const value: ExternalDesignProviderContext = {
    design,
    addToDesign,
    logosOfDesign: designLogos,
    selectedLogo: designLogos.find((l) => l.inDesignId === selectedLogoId),
    selectedView: selectedView!,
    views: views,
    style: style,
    lockedLogos,
    lockLogo: handleLockLogo,
    rotateLogo,
    selectView: selectView,
    imageRef: imageRef,
    containerRef: containerRef,
    onImageRefSet,
    scaledViewInfo: scaledViewInfoPerView[selectedView!.id],
    selectLogo: setSelectedLogoId,
    scaledLogosOfDesign: activeViewMarkers ?? [],
    logoPlacement: logoPlacement,
    parentContainerXnY: parentContainerXnY,
    onLogoManipulation: (marker) => onLogoManipulation(marker).then(),
    removeFromDesign,
    placementErrorMessage: buildPlacementErrorMessage(),
    readonly: false,
    debug: DEBUG_LOGO_PLACER,
    lastCoverageChecked,
    debugPoints,
    changeLogoView,
    config,
    apiKey,
  };

  return (
    <ExternalDesignContext.Provider value={value}>
      {children}
    </ExternalDesignContext.Provider>
  );
}

function useExternalDesignContext() {
  const context = useContext(ExternalDesignContext);
  if (!context)
    throw new Error(
      "useExternalDesignContext must be used within a ExternalDesignProvider",
    );
  return context;
}

export { ExternalDesignProvider, useExternalDesignContext };
